import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, Scales } from "@phosphor-icons/react";
import { extractErrorMessage } from "@/lib/apiError";

const C = { bg: "#000", ink: "#0A2826", panel: "#103532", teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B", amber: "#FCD34D" };

export default function ValuationNew() {
  const navigate = useNavigate();
  const [company, setCompany] = useState("");
  const [stage, setStage] = useState("post_revenue");
  const [sector, setSector] = useState("saas");
  const [submitting, setSubmitting] = useState(false);

  const create = async () => {
    if (!company.trim()) { toast.error("Please enter a company name."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/valuations", {
        company_name: company.trim(),
        stage,
        sub_stage: stage === "pre_revenue" ? "mvp" : "growth",
        sector,
        currency: "USD",
      });
      navigate(`/valuation/${data.valuation_id}`);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not create valuation"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: C.bg, fontFamily: "Manrope, sans-serif" }} data-testid="valuation-new">
      <header className="border-b" style={{ borderColor: "rgba(123,211,206,0.12)" }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white">
            <ArrowLeft size={14} /> Back
          </Link>
          <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em]"
            style={{ color: C.amber }}>
            <Scales size={11} weight="fill" /> Business valuation · Methodology v1
          </div>
          <span />
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-[1.05]">
          Start a <span style={{ color: C.amber }}>defensible</span> valuation.
        </h1>
        <p className="text-white/65 mt-4 text-base leading-relaxed max-w-2xl">
          We&apos;ll run the full Clinq methodology — Scorecard, Berkus and Cost-to-Duplicate for pre-revenue;
          Revenue Multiple, EV/EBITDA, DCF and NAV for post-revenue — and produce a three-value report you
          can hand to investors.
        </p>

        <div className="mt-10 rounded-2xl p-6 border space-y-5"
          style={{ borderColor: "rgba(123,211,206,0.18)", backgroundColor: C.ink }}>
          <Field label="Company name">
            <input type="text" value={company} onChange={(e) => setCompany(e.target.value)}
              data-testid="new-valuation-company"
              placeholder="e.g. Acme Mobility"
              className="w-full px-3 py-2.5 rounded-md border focus:outline-none"
              style={{ backgroundColor: "rgba(0,0,0,0.45)", borderColor: "rgba(255,255,255,0.1)", color: "#fff" }} />
          </Field>
          <Field label="Where are you in the journey?">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { id: "pre_revenue", label: "Pre-revenue", note: "Idea / MVP / pilot — methods: Berkus, Scorecard, Cost-to-Duplicate" },
                { id: "post_revenue", label: "Post-revenue", note: "Trading 6+ months — methods: Revenue Multiple, EV/EBITDA, DCF, NAV" },
              ].map((s) => (
                <button key={s.id} onClick={() => setStage(s.id)} data-testid={`new-valuation-stage-${s.id}`}
                  className="text-left p-4 rounded-md border transition-colors"
                  style={{
                    backgroundColor: stage === s.id ? "rgba(252, 211, 77, 0.06)" : "rgba(0,0,0,0.3)",
                    borderColor: stage === s.id ? `${C.amber}` : "rgba(255,255,255,0.08)",
                  }}>
                  <div className="font-bold text-white">{s.label}</div>
                  <div className="text-[12px] mt-1.5" style={{ color: "rgba(255,255,255,0.55)" }}>{s.note}</div>
                </button>
              ))}
            </div>
          </Field>
          <Field label="Sector">
            <select value={sector} onChange={(e) => setSector(e.target.value)}
              data-testid="new-valuation-sector"
              className="w-full px-3 py-2.5 rounded-md border focus:outline-none"
              style={{ backgroundColor: "rgba(0,0,0,0.45)", borderColor: "rgba(255,255,255,0.1)", color: "#fff" }}>
              {[
                ["fintech", "Fintech / Digital Financial Services"],
                ["agritech", "Agritech / Agriculture"],
                ["healthtech", "Health tech / Healthcare"],
                ["edtech", "Edu tech / Education"],
                ["ecommerce", "E-commerce / Retail tech"],
                ["logistics", "Logistics / Supply chain"],
                ["saas", "SaaS / Enterprise software"],
                ["cleantech", "CleanTech / Energy"],
                ["manufacturing", "Manufacturing / Industrials"],
                ["services", "Professional services"],
                ["proptech", "Proptech / Real estate tech"],
                ["media", "Media & content"],
                ["insurtech", "Insurtech"],
                ["marketplace", "Marketplace / Platform"],
                ["other", "Other / Diversified"],
              ].map(([id, label]) => <option key={id} value={id} style={{ backgroundColor: C.ink }}>{label}</option>)}
            </select>
          </Field>
          <button onClick={create} disabled={submitting}
            data-testid="new-valuation-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-extrabold transition-colors disabled:opacity-60"
            style={{ backgroundColor: C.amber, color: "#000" }}>
            {submitting ? "Creating…" : "Start valuation"} <ArrowRight size={14} weight="bold" />
          </button>
          <p className="text-[11px] text-center" style={{ color: "rgba(255,255,255,0.4)" }}>
            Editing and preview are free. Finalising the report costs 50 credits.
          </p>
        </div>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1.5" style={{ color: "rgba(255,255,255,0.55)" }}>{label}</div>
      {children}
    </label>
  );
}
